import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { SERVICES } from "@/lib/index";

const springGentle = { type: "spring" as const, stiffness: 300, damping: 35 };

export default function ServicesSection() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  const kiServices = SERVICES.filter((s) => s.category === "ki");
  const socialServices = SERVICES.filter((s) => s.category === "social");

  return (
    <section id="leistungen" className="py-28 relative" ref={ref}>
      {/* Section header */}
      <div className="container mx-auto px-6 mb-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={springGentle}
          className="text-center max-w-3xl mx-auto"
        >
          <span
            className="inline-block text-xs font-semibold uppercase tracking-[0.2em] mb-4 px-3 py-1 rounded-full border"
            style={{ color: "oklch(0.72 0.20 55)", borderColor: "oklch(0.72 0.20 55 / 0.3)", background: "oklch(0.72 0.20 55 / 0.08)" }}
          >
            Unsere Leistungen
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-foreground mb-5">
            Alles aus <span style={{ color: "oklch(0.72 0.20 55)" }}>einer Hand</span>
          </h2>
          <p className="text-muted-foreground text-lg leading-relaxed">
            Kreative KI-Werbung trifft professionelles Social-Media-Management – für einen starken, einheitlichen Markenauftritt.
          </p>
        </motion.div>
      </div>

      <div className="container mx-auto px-6">
        {/* KI Services */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ ...springGentle, delay: 0.15 }}
          className="flex items-center gap-3 mb-8"
        >
          <div
            className="h-px flex-1 opacity-20"
            style={{ background: "oklch(0.60 0.18 185)" }}
          />
          <span
            className="text-sm font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full border"
            style={{ color: "oklch(0.60 0.18 185)", borderColor: "oklch(0.60 0.18 185 / 0.4)", background: "oklch(0.60 0.18 185 / 0.08)" }}
          >
            🤖 KI-Werbung & Design
          </span>
          <div
            className="h-px flex-1 opacity-20"
            style={{ background: "oklch(0.60 0.18 185)" }}
          />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {kiServices.map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...springGentle, delay: 0.2 + i * 0.1 }}
              whileHover={{ y: -6, transition: { type: "spring", stiffness: 400, damping: 25 } }}
              className="group relative rounded-2xl bg-card border border-border p-7 hover:border-primary/40 transition-colors overflow-hidden"
              style={{ boxShadow: "0 8px 30px -6px oklch(0.60 0.18 185 / 0.12)" }}
            >
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: "radial-gradient(ellipse at top left, oklch(0.60 0.18 185 / 0.06) 0%, transparent 60%)" }}
              />
              <div className="relative z-10">
                <div className="text-4xl mb-5">{service.icon}</div>
                <h3 className="font-heading text-xl font-bold text-foreground mb-3">{service.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-5">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Social Media Services */}
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          animate={inView ? { opacity: 1, x: 0 } : {}}
          transition={{ ...springGentle, delay: 0.4 }}
          className="flex items-center gap-3 mb-8"
        >
          <div
            className="h-px flex-1 opacity-20"
            style={{ background: "oklch(0.72 0.20 55)" }}
          />
          <span
            className="text-sm font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full border"
            style={{ color: "oklch(0.72 0.20 55)", borderColor: "oklch(0.72 0.20 55 / 0.4)", background: "oklch(0.72 0.20 55 / 0.08)" }}
          >
            📱 Social-Media-Management
          </span>
          <div
            className="h-px flex-1 opacity-20"
            style={{ background: "oklch(0.72 0.20 55)" }}
          />
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {socialServices.map((service, i) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 40 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...springGentle, delay: 0.45 + i * 0.1 }}
              whileHover={{ y: -6, transition: { type: "spring", stiffness: 400, damping: 25 } }}
              className="group relative rounded-2xl bg-card border border-border p-7 hover:border-primary/40 transition-colors overflow-hidden"
              style={{ boxShadow: "0 8px 30px -6px oklch(0.72 0.20 55 / 0.12)" }}
            >
              <div
                className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{ background: "radial-gradient(ellipse at top left, oklch(0.72 0.20 55 / 0.06) 0%, transparent 60%)" }}
              />
              <div className="relative z-10">
                <div className="text-4xl mb-5">{service.icon}</div>
                <h3 className="font-heading text-xl font-bold text-foreground mb-3">{service.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed mb-5">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
